import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo } from "react";
import { BoardListCard } from "../../../cores/boards/interfaces/api/get-board";
import { useBoardCore } from "../../../cores/boards";
import Avatar from "../Avatar";
import { TooltipWembii } from "../TooltipWembii";
import { UserStatus } from "../../../cores/user/constants/user-status";
import { Badge } from "reactstrap";
import { COLOR_DANGER } from "../../../constants/theme";
import { getFirstLetters } from "../../utils/stringUtils";
import { useGlobalCore } from "../../../cores/globals";
import { BOARD_CARD_MODAL } from "../../../constants/modal.constant";
import { useMeCore } from "../../../cores/me";
import { useCompanyCore } from "../../../cores/company";

interface Props {
  card: BoardListCard;
}

export default function OpportunityCard(props: Props) {
  const { card } = props;
  const { turnOnModal } = useGlobalCore();
  const { company } = useMeCore();
  const { getCompany } = useCompanyCore();
  const { getCard, getLists, getCustomFields } = useBoardCore();

  const onClick = useCallback(() => {
    const listId = card.listId || card.list;
    const boardId = card.boardId;
    if (!company?.id || !boardId || !listId || !card.id) return;

    getCard(company.id, boardId, listId, card.id, true);
    getCustomFields(company.id, boardId);
    getLists(company.id, boardId);
    getCompany(company.id);
    turnOnModal(BOARD_CARD_MODAL, true);
  }, [
    card,
    company,
    getCard,
    getCompany,
    getCustomFields,
    getLists,
    turnOnModal,
  ]);

  const contacts = card.contacts || [];
  const companies = card.companies || [];
  const tags = card.tags || [];
  const assignments = card.assignments || [];

  const contactsAssigned = useMemo(() => {
    if (companies.length > 0) {
      return contacts.slice(0, 1);
    } else {
      return contacts.slice(0, 2);
    }
  }, [companies, contacts]);

  const contactsRemainingQuantity = useMemo(() => {
    if (companies.length > 0) {
      return contacts.length - 1;
    } else {
      return contacts.length - 2;
    }
  }, [companies, contacts]);

  const companiesAssigned = useMemo(() => {
    if (contacts.length > 0) {
      return companies.slice(0, 1);
    } else {
      return companies.slice(0, 2);
    }
  }, [companies, contacts]);

  const companiesRemainingQuantity = useMemo(() => {
    if (contacts.length > 0) {
      return companies.length - 1;
    } else {
      return companies.length - 2;
    }
  }, [companies, contacts]);

  const totalRemainingQuantity = useMemo(() => {
    let quantity =
      companiesRemainingQuantity > 0 ? companiesRemainingQuantity : 0;
    quantity =
      contactsRemainingQuantity > 0
        ? contactsRemainingQuantity + quantity
        : quantity;
    return quantity;
  }, [contactsRemainingQuantity, companiesRemainingQuantity]);

  const assigned = useMemo(() => {
    return assignments.length === 1 ? assignments[0] : null;
  }, [assignments]);

  const assigmentNames = useMemo(() => {
    return assignments
      .map((assignment) => `${assignment.firstName} ${assignment.lastName}`)
      .join(" - ");
  }, [assignments]);

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "card card-panel px-3 py-2 pointer mb-2 border rounded my-2"
      )}
    >
      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-column justify-content-between">
          <div className="d-flex flex-row flex-wrap align-items-center">
            {tags.map((tag) => (
              <Badge
                pill
                style={{ backgroundColor: tag.backgroundColor }}
                className={cn("text-left px-2 my-1 mr-1 pointer", {
                  labelsExpanded: true,
                })}
                key={tag.id}
              >
                <span
                  className="h6 mb-0 font-weight-bold"
                  style={{ color: tag.textColor, fontSize: 11.2 }}
                >
                  {tag.name}
                </span>
              </Badge>
            ))}
          </div>
          <div
            className="overflow-hidden font-weight-bold"
            style={{ fontSize: "1.1rem" }}
          >
            {card.title}
          </div>

          {contacts.length > 0 || companies.length > 0 ? (
            <div>
              <div className="d-flex flex-wrap">
                {contactsAssigned.map((contact) => (
                  <div
                    key={contact.id}
                    style={{ maxHeight: 24, maxWidth: 190 }}
                    className="truncate-label my-1 text-dark-50 font-weight-bold pointer px-2 border border-warning rounded-pill position-relative"
                  >
                    {contact.status !== UserStatus.ACTIVE.id && (
                      <FontAwesomeIcon
                        icon={["fas", "ban"]}
                        size="1x"
                        style={{
                          top: "-5px",
                          right: "-5px",
                        }}
                        className="position-absolute "
                        color={COLOR_DANGER}
                      />
                    )}
                    {contact.name}
                  </div>
                ))}
                {companiesAssigned.map((companyAssigned) => (
                  <div
                    key={companyAssigned.id}
                    style={{ maxHeight: 24, maxWidth: 190 }}
                    className="truncate-label my-1 text-dark-50 font-weight-bold pointer px-2 border border-primary rounded-pill"
                  >
                    {companyAssigned.name}
                  </div>
                ))}
                {totalRemainingQuantity > 0 && (
                  <div className="text-dark-50 font-weight-bold pointer m-1 px-2">
                    {`+${totalRemainingQuantity}`}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="d-flex flex-column justify-content-between">
          {assigned && (
            <>
              <Avatar
                id={`user-${assigned.id}-${card.id}`}
                active={assigned.status === UserStatus.ACTIVE.id}
                className="text-primary font-weight-bold m-1"
                label={getFirstLetters(
                  `${assigned.lastName} ${assigned.firstName}`
                )}
                image={assigned.avatarUrl}
              />
              <TooltipWembii
                id={`user-${assigned.id}-${card.id}`}
                text={assigmentNames}
              />
            </>
          )}

          {assignments.length > 1 && (
            <>
              <Avatar
                id="multiple-assignment"
                className="text-primary font-weight-bold m-1"
                label={`${assignments.length}`}
              />
              <TooltipWembii id="multiple-assignment" text={assigmentNames} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
